import { useEffect, useState } from 'react';
import { setClassroomTimezone } from '../../lib/api';
import { Link } from 'react-router-dom';
import { minutes, shortDate, today } from '../../lib/dates';
import type { Assignment, AssignmentKind, StudentAccount } from '../../lib/types';
import { thumbnailUrl } from '../../lib/youtube';
import { KIND, byDue, goesOutOn, isOpen, isOut, requirement } from '../../lib/kinds';
import { TOPICS } from '../../lib/grammar/topics';
import { useClassroom } from '../../state/Classroom';
import CreateClassForm from './CreateClassForm';
import MeetingCard from '../../components/MeetingCard';

// ─────────────────────────────────────────────────────────────────────────────
// The teacher's home. One screen that answers, in order:
//   - what's open now (by due date, not weeks), and how far along is the class?
//   - who hasn't started?
//   - what just happened?
// Everything links through to the page that acts on it.
// ─────────────────────────────────────────────────────────────────────────────

type Status = 'done' | 'started' | 'todo';

export default function Dashboard() {
  const { active, students, assignments, progressFor, passesFor, dueFor } = useClassroom();
  const [copied, setCopied] = useState(false);

  // Classes made before time zones existed get the teacher's, once.
  useEffect(() => {
    if (active && !active.timezone) {
      void setClassroomTimezone(active.id, Intl.DateTimeFormat().resolvedOptions().timeZone).catch(() => {});
    }
  }, [active]);

  if (!active) {
    return (
      <div className="narrow">
        <h1>Set up your class</h1>
        <p className="sub">
          Create a class to get a 6-character code. Students type it when they
          make their account, and that puts them on your roster.
        </p>
        <CreateClassForm />
      </div>
    );
  }

  // What students can work on now (sent out, not past due), soonest due first; no weeks.
  const thisWeek = assignments.filter(a => isOpen(a, today(), students.map(s => dueFor(a, s.user_id)))).sort(byDue);
  const upcoming = assignments.filter(a => !isOut(a, today()));
  const video = thisWeek.find(a => a.kind === 'video');
  const story = thisWeek.find(a => a.kind === 'story');
  const grammar = thisWeek.find(a => a.kind === 'grammar');

  const statusOf = (a: Assignment, s: StudentAccount): Status => {
    const p = progressFor(a.id, s.user_id);
    return p?.completed_at ? 'done' : p ? 'started' : 'todo';
  };

  // ── Stats ────────────────────────────────────────────────────────────────
  const slots = thisWeek.length * students.length;
  const doneSlots = thisWeek.reduce((n, a) => n + students.filter(s => statusOf(a, s) === 'done').length, 0);
  const allDone = students.filter(s => thisWeek.length > 0 && thisWeek.every(a => statusOf(a, s) === 'done')).length;
  const notStarted = students.filter(s => thisWeek.length > 0 && thisWeek.every(a => statusOf(a, s) === 'todo')).length;
  const pct = slots > 0 ? Math.round((doneSlots / slots) * 100) : 0;

  // ── Needs attention: anyone with an unfinished open assignment ───────
  const behind = students
    .map(s => ({
      s,
      todo: thisWeek.filter(a => statusOf(a, s) !== 'done').map(a => ({ a, status: statusOf(a, s) })),
    }))
    .filter(x => x.todo.length > 0)
    // Not started at all first, then the rest.
    .sort((x, y) => Number(y.todo.every(t => t.status === 'todo')) - Number(x.todo.every(t => t.status === 'todo'))
      || x.s.display_name.localeCompare(y.s.display_name));

  // ── Recent activity: finished passes across the class ─────────────────────
  const names = new Map(students.map(s => [s.user_id, s.display_name]));
  const byId = new Map(assignments.map(a => [a.id, a]));
  const recent = assignments
    .flatMap(a => students.flatMap(s => passesFor(a.id, s.user_id)))
    .sort((x, y) => y.finished_at.localeCompare(x.finished_at))
    .slice(0, 10);

  const signupLink = `${window.location.origin}/?code=${active.join_code}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(signupLink); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { window.prompt('Copy this link:', signupLink); }
  };

  return (
    <div className="dash">
      <header className="dash-head">
        <div>
          <h1>{active.name}</h1>
        </div>
        <div className="code-chip">
          <span className="muted small">Class code</span>
          <span className="code">{active.join_code}</span>
          <button className="small" onClick={copy}>{copied ? 'Copied ✓' : 'Copy sign-up link'}</button>
        </div>
      </header>

      {active.meet_url
        ? <MeetingCard c={active} teacher />
        : <Link className="meeting-nudge small" to="/roster#meeting">📹 Add your Google Meet or Zoom link so students can join class from here</Link>}

      <section className="stats">
        <Stat n={students.length} label="Students" to="/roster" />
        <Stat n={`${pct}%`} label="Of open work done" to="/progress" />
        <Stat n={allDone} label="Finished everything" to="/progress" />
        <Stat n={notStarted} label="Haven't started" to="/progress" warn={notStarted > 0} />
      </section>

      <section className="week-cards">
        <WeekCard kind="video" a={video} students={students} statusOf={statusOf} />
        <WeekCard kind="story" a={story} students={students} statusOf={statusOf} />
        <WeekCard kind="grammar" a={grammar} students={students} statusOf={statusOf} />
      </section>

      <section className="dash-cols">
        <div className="panel">
          <div className="panel-head">
            <h3>Needs attention</h3>
            <Link to="/progress" className="small">Full progress →</Link>
          </div>
          {thisWeek.length === 0
            ? <p className="muted small">Nothing open right now.</p>
            : students.length === 0
              ? <p className="muted small">No students yet. Share the class code.</p>
              : behind.length === 0
                ? <p className="good-text small">Everyone has finished their open work. 🎉</p>
                : (
                  <ul className="rows">
                    {behind.slice(0, 12).map(({ s, todo }) => (
                      <li key={s.user_id}>
                        <span className="grow">
                          <span className="term">{s.display_name}</span>
                          <span className="small muted">{s.username}</span>
                        </span>
                        {todo.map(({ a, status }) => {
                          const p = progressFor(a.id, s.user_id);
                          return (
                            <span key={a.id} className={`status ${status}`}>
                              {KIND[a.kind].icon}{' '}
                              {status === 'todo'
                                ? (a.due_date && a.due_date < today() ? 'Missing' : 'Not started')
                                : `${p?.completions ?? 0}/${a.required_count}`}
                            </span>
                          );
                        })}
                      </li>
                    ))}
                    {behind.length > 12 && <li className="muted small">and {behind.length - 12} more…</li>}
                  </ul>
                )}
        </div>

        <div className="panel">
          <div className="panel-head"><h3>Recent activity</h3></div>
          {recent.length === 0
            ? <p className="muted small">Finished watches and reads will show up here.</p>
            : (
              <ul className="rows feed">
                {recent.map(p => {
                  const a = byId.get(p.assignment_id)!;
                  return (
                    <li key={`${p.assignment_id}:${p.student_id}:${p.n}`}>
                      <span className="grow">
                        <span><strong>{names.get(p.student_id)}</strong> finished {KIND[a.kind].verb} {p.n} of{' '}
                          <Link to={`/a/${a.id}`}>{a.title}</Link></span>
                        <span className="small muted">
                          {a.kind === 'grammar' && p.total ? `${Math.round(((p.correct ?? 0) / p.total) * 100)}% right · ` : ''}
                          {minutes(p.seconds)} {KIND[a.kind].doneWord}
                          {a.kind === 'story' && ` · ${p.lookups} lookup${p.lookups === 1 ? '' : 's'}`}
                        </span>
                      </span>
                      <span className="small muted nowrap">{shortDate(p.finished_at)}</span>
                    </li>
                  );
                })}
              </ul>
            )}
        </div>
      </section>

      {upcoming.length > 0 && (
        <section>
          <h2>Coming up</h2>
          <div className="list">
            {upcoming.map(a => (
              <Link key={a.id} to={`/assign/${a.id}`} className="item link">
                <span>{KIND[a.kind].icon}</span>
                <span className="grow">
                  <span className="term">{a.title}</span>
                  <span className="gloss small">Goes out {shortDate(goesOutOn(a))}</span>
                </span>
                <span className="small muted">Edit</span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({ n, label, to, warn }: { n: number | string; label: string; to: string; warn?: boolean }) {
  return (
    <Link to={to} className={`stat${warn ? ' warn' : ''}`}>
      <span className="n">{n}</span>
      <span className="k">{label}</span>
    </Link>
  );
}

function WeekCard({ kind, a, students, statusOf }: {
  kind: AssignmentKind;
  a: Assignment | undefined;
  students: StudentAccount[];
  statusOf: (a: Assignment, s: StudentAccount) => Status;
}) {
  const label = KIND[kind].thisWeek;
  if (!a) {
    return (
      <div className="week-card empty">
        <div className="badge">{label}</div>
        <p className="muted">Nothing assigned yet.</p>
        <Link to={`/assign?kind=${kind}`} className="button primary-link">
          + Assign {kind === 'grammar' ? 'grammar' : `a ${kind}`}
        </Link>
      </div>
    );
  }

  const counts = { done: 0, started: 0, todo: 0 };
  students.forEach(s => { counts[statusOf(a, s)] += 1; });
  const total = Math.max(students.length, 1);

  return (
    <div className="week-card">
      <div className="week-card-top">
        {a.kind === 'video'
          ? <img src={thumbnailUrl(a.video_id!)} alt="" className="cover" />
          : a.kind === 'grammar'
            ? <div className="cover grammar-cover">{(a.grammar?.topics ?? []).map(t => TOPICS[t].emoji).join(' ')}</div>
            : <div className="cover story-cover">{(a.story_text ?? '').slice(0, 140)}…</div>}
        <div className="grow">
          <div className="badge">{label}</div>
          <Link to={`/a/${a.id}`} className="title">{a.title}</Link>
          <div className="small muted">
            {requirement(a, true)}
            {a.due_date ? ` · due ${shortDate(a.due_date)}` : ''}
          </div>
        </div>
      </div>

      <div className="stack-bar" aria-label={`${counts.done} done, ${counts.started} in progress, ${counts.todo} not started`}>
        <i className="done" style={{ width: `${(counts.done / total) * 100}%` }} />
        <i className="started" style={{ width: `${(counts.started / total) * 100}%` }} />
      </div>
      <div className="legend small">
        <span><b className="dot done" /> {counts.done} done</span>
        <span><b className="dot started" /> {counts.started} in progress</span>
        <span><b className="dot todo" /> {counts.todo} not started</span>
      </div>

      <div className="row" style={{ marginTop: 12 }}>
        <Link to={`/a/${a.id}`} className="button small">Preview</Link>
        <Link to={`/assign/${a.id}`} className="button small">Edit</Link>
      </div>
    </div>
  );
}
